import { useState, useRef, useCallback, useEffect } from "react";
import cloudApi from "../../../services/cloudApi";

/**
 * Hook for managing column editing operations in cloud mode
 * Supports column rename, type change, and delete with optimistic updates
 */
export const useCloudColumnEditing = ({ tableId, columns, setColumns }) => {
  // State for column menu
  const [columnMenuOpen, setColumnMenuOpen] = useState(null);
  const columnMenuRef = useRef(null);

  // State for column editing
  const [editingColumnId, setEditingColumnId] = useState(null);
  const [editColumnName, setEditColumnName] = useState("");
  const [editColumnType, setEditColumnType] = useState("text");

  // State for delete operation
  const [deletingColumn, setDeletingColumn] = useState(null);
  const [deletedColumnName, setDeletedColumnName] = useState(null);

  // Column type options
  const columnTypeOptions = [
    { value: "text", label: "Text", emoji: "📝" },
    { value: "number", label: "Number", emoji: "🔢" },
    { value: "price", label: "Price", emoji: "💰" },
    { value: "date", label: "Date", emoji: "📅" },
    { value: "link", label: "Link", emoji: "🔗" },
    { value: "image", label: "Image", emoji: "📷" },
    { value: "email", label: "Email", emoji: "📧" },
    { value: "phone", label: "Phone", emoji: "📞" },
    { value: "boolean", label: "Boolean", emoji: "✓" },
    { value: "json", label: "JSON", emoji: "{ }" },
  ];

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setColumnMenuOpen(null);
      }
    };

    if (columnMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [columnMenuOpen]);

  // Toggle column menu
  const handleColumnMenuToggle = useCallback((columnId, event) => {
    event?.stopPropagation();
    setColumnMenuOpen((prev) => (prev === columnId ? null : columnId));
  }, []);

  // Start editing a column
  const handleColumnEditStart = useCallback((column) => {
    setEditingColumnId(column.id);
    setEditColumnName(column.name || "");
    setEditColumnType(column.type || "text");
    setColumnMenuOpen(null);
  }, []);

  // Save column edits (rename and/or type change)
  const handleColumnEditSave = useCallback(async () => {
    if (!editingColumnId || !tableId || !columns) return;

    const column = columns.find((col) => col.id === editingColumnId);
    if (!column) return;

    // Check if anything changed
    const nameChanged = editColumnName !== column.name;
    const typeChanged = editColumnType !== column.type;

    if (!nameChanged && !typeChanged) {
      setEditingColumnId(null);
      return;
    }

    // Build updates object
    const updates = {};
    if (nameChanged) updates.name = editColumnName;
    if (typeChanged) updates.type = editColumnType;

    // Store previous state for rollback
    const previousColumns = [...columns];

    // Optimistic update
    setColumns(
      columns.map((col) =>
        col.id === editingColumnId
          ? { ...col, name: editColumnName, type: editColumnType }
          : col
      )
    );
    setEditingColumnId(null);

    try {
      await cloudApi.updateColumn(tableId, editingColumnId, updates);
    } catch (error) {
      // Rollback on error
      console.error("Failed to update column:", error);
      setColumns(previousColumns);
      alert("Failed to update column. Please try again.");
    }
  }, [tableId, columns, setColumns, editingColumnId, editColumnName, editColumnType]);

  // Delete a column
  const handleColumnDelete = useCallback(async (columnId) => {
    if (!tableId || !columns || deletingColumn) return;

    const column = columns.find((col) => col.id === columnId);
    if (!column) return;

    const columnName = column.name || column.id;

    // Store previous state for rollback
    const previousColumns = [...columns];

    // Set deleting state
    setDeletingColumn(columnId);
    setColumnMenuOpen(null);

    // Optimistic update - remove column immediately
    setColumns(columns.filter((col) => col.id !== columnId));

    try {
      await cloudApi.deleteColumn(tableId, columnId);
      // Show success feedback
      setDeletedColumnName(columnName);
    } catch (error) {
      // Rollback on error
      console.error("Failed to delete column:", error);
      setColumns(previousColumns);
      alert("Failed to delete column. Please try again.");
    } finally {
      setDeletingColumn(null);
    }
  }, [tableId, columns, setColumns, deletingColumn]);

  // Clear delete feedback
  const clearDeleteFeedback = useCallback(() => {
    setDeletedColumnName(null);
  }, []);

  return {
    // Menu state
    columnMenuOpen,
    setColumnMenuOpen,
    columnMenuRef,
    handleColumnMenuToggle,

    // Edit state
    editingColumnId,
    setEditingColumnId,
    editColumnName,
    setEditColumnName,
    editColumnType,
    setEditColumnType,
    handleColumnEditStart,
    handleColumnEditSave,

    // Delete state
    deletingColumn,
    handleColumnDelete,
    deletedColumnName,
    clearDeleteFeedback,

    // Options
    columnTypeOptions,
  };
};

export default useCloudColumnEditing;
